import { createContext, useContext, useLayoutEffect, useState } from "react";
import type { PropsWithChildren } from "react";

import { LogoLoader } from "@/shared/components";
import type { Subscription } from "@/shared/domains";
import type { ObjectValue } from "@/shared/models/legacy-types";
import { useCancellationDiscount } from "../hooks";
import type { CancellationReason } from "../models";

export const CANCEL_SUBSCRIPTION_MODAL_STEPS = {
  REASONS: "reasons",
  DISCOUNT: "discount",
  EMAIL_SENT: "email_sent",
  FAREWELL: "farewell",
} as const;

type ModalStep = ObjectValue<typeof CANCEL_SUBSCRIPTION_MODAL_STEPS>;

interface CancelSubscriptionContextValue {
  currentModal: ModalStep;
  show: boolean;
  reasons: CancellationReason["id"][];
  subscription: Subscription;

  actions: {
    setCurrentModal: (currentModal: ModalStep) => void;
    onClose: VoidFunction;
    setReasons: (reasons: CancellationReason["id"][]) => void;
  };
}

const CancelSubscriptionContext =
  createContext<CancelSubscriptionContextValue | null>(null);

export const useCancelSubscriptionContext = () => {
  const context = useContext(CancelSubscriptionContext);

  if (!context) {
    throw new Error(
      "useCancelSubscriptionContext must be used within a <CancelSubscriptionProvider />",
    );
  }

  return context;
};

interface CancelSubscriptionProviderProps extends PropsWithChildren {
  show: boolean;
  onClose: VoidFunction;
  subscription: Subscription;
}

export const CancelSubscriptionProvider = ({
  show,
  onClose,
  subscription,
  children,
}: CancelSubscriptionProviderProps) => {
  const [currentModal, setCurrentModal] = useState<ModalStep>(
    CANCEL_SUBSCRIPTION_MODAL_STEPS.REASONS,
  );
  const [reasons, setReasons] = useState<CancellationReason["id"][]>([]);

  useLayoutEffect(() => {
    if (show) {
      setCurrentModal(CANCEL_SUBSCRIPTION_MODAL_STEPS.REASONS);
    }
  }, [show]);

  const { isLoading, isSuccess } = useCancellationDiscount({
    subscriptionId: subscription.id,
    enabled: show,
  });

  return (
    <>
      <LogoLoader show={isLoading} fullscreen />

      {isSuccess && (
        <CancelSubscriptionContext.Provider
          value={{
            show,
            currentModal,
            reasons,
            subscription,

            actions: { setCurrentModal, onClose, setReasons },
          }}
        >
          {children}
        </CancelSubscriptionContext.Provider>
      )}
    </>
  );
};
